import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Header from '@/components/layout/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Calendar, MapPin, Eye, User, Heart, Bookmark, Plus, X, Loader2 } from 'lucide-react';
import { getImageUrl } from '@/utils/helpers';
import { format } from 'date-fns';
import { zhCN } from 'date-fns/locale';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExhibitionDetailPage = () => {
  const { exhibitionId } = useParams();
  const [exhibition, setExhibition] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddArtworks, setShowAddArtworks] = useState(false);
  const [myArtworks, setMyArtworks] = useState([]);
  const [selectedArtworks, setSelectedArtworks] = useState([]);
  const [addingArtworks, setAddingArtworks] = useState(false);
  const [artworkSearch, setArtworkSearch] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();

  // 判断当前用户是否是展览创建者
  const isCreator = user && exhibition && String(exhibition.created_by) === String(user.id);
  const isAdmin = user?.role === 'admin';
  const canManageArtworks = isCreator || isAdmin;

  // 调试信息（生产环境可删除）
  useEffect(() => {
    if (exhibition && user) {
      console.log('Exhibition created_by:', exhibition.created_by);
      console.log('Current user id:', user.id);
      console.log('isCreator:', isCreator);
      console.log('canManageArtworks:', canManageArtworks);
    }
  }, [exhibition, user]);

  useEffect(() => {
    fetchExhibition();
  }, [exhibitionId]);

  const fetchExhibition = async () => {
    try {
      const res = await axios.get(`${API}/exhibitions/${exhibitionId}`);
      setExhibition(res.data);
    } catch (err) {
      console.error('Failed to fetch exhibition:', err);
      toast.error('画展不存在或已下线');
    } finally {
      setIsLoading(false);
    }
  };

  // 获取可添加到展览的作品列表
  // 策展人（展览创建者）只能添加自己的作品，管理员可以添加所有作品
  const fetchAvailableArtworks = async () => {
    try {
      console.log('Fetching artworks...');
      console.log('Current user:', user?.id, 'isAdmin:', isAdmin, 'isCreator:', isCreator);
      
      let available = [];
      
      if (isAdmin) {
        // 管理员可以看到所有作品（包括用户发布的pending状态作品）
        const res = await axios.get(`${API}/artworks?include_user_artworks=true`);
        available = res.data || [];
        console.log('Admin: fetched all artworks:', available.length);
      } else if (user?.id) {
        // 非管理员只能看到自己的作品（包括pending状态）
        const res = await axios.get(`${API}/artworks?creator_id=${user.id}`);
        available = res.data || [];
        console.log('Creator: fetched own artworks:', available.length);
      }
      
      // 获取已在展览中的作品ID
      const existingIds = new Set(exhibition?.artwork_ids || []);
      console.log('Exhibition artwork_ids:', exhibition?.artwork_ids);
      
      // 过滤掉已经在展览中的作品
      available = available.filter(a => !existingIds.has(a.id));
      console.log('Available artworks after filter:', available.length);
      
      setMyArtworks(available);
    } catch (err) {
      console.error('Failed to fetch artworks:', err);
      toast.error('获取作品列表失败');
    }
  };

  // 打开添加作品弹窗
  const handleOpenAddArtworks = () => {
    console.log('Opening add artworks dialog');
    console.log('Current exhibition:', exhibition);
    fetchAvailableArtworks();
    setSelectedArtworks([]);
    setArtworkSearch('');
    setShowAddArtworks(true);
  };

  // 选择/取消选择作品
  const toggleArtworkSelection = (artworkId) => {
    setSelectedArtworks(prev => 
      prev.includes(artworkId) 
        ? prev.filter(id => id !== artworkId)
        : [...prev, artworkId]
    );
  };

  // 添加作品到展览
  const handleAddArtworks = async () => {
    if (selectedArtworks.length === 0) {
      toast.error('请选择至少一件作品');
      return;
    }
    
    setAddingArtworks(true);
    try {
      await axios.post(`${API}/exhibitions/${exhibitionId}/artworks`, 
        { artwork_ids: selectedArtworks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`成功添加 ${selectedArtworks.length} 件作品`);
      setShowAddArtworks(false);
      fetchExhibition(); // 刷新展览数据
    } catch (err) {
      console.error('Failed to add artworks:', err);
      toast.error(err.response?.data?.detail || '添加失败');
    } finally {
      setAddingArtworks(false);
    }
  };

  // 从展览移除作品
  const handleRemoveArtwork = async (artworkId) => {
    if (!confirm('确定要从展览中移除这件作品吗？')) return;
    
    try {
      await axios.delete(`${API}/exhibitions/${exhibitionId}/artworks`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { artwork_ids: [artworkId] }
      });
      toast.success('作品已移除');
      fetchExhibition();
    } catch (err) {
      console.error('Failed to remove artwork:', err);
      toast.error('移除失败');
    }
  };

  const getLocalizedField = (item, field) => {
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    return item[field] || '';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'yyyy年M月d日', { locale: zhCN });
    } catch {
      return dateStr;
    }
  };

  // 用户类型标签
  const getUserTypeLabel = (type) => {
    const types = {
      artist: '艺术家',
      collector: '收藏家',
      museum: '美术馆',
      curator: '策展人'
    };
    return types[type] || '艺术家';
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-amber-300 border-t-amber-600 rounded-full"></div>
        </div>
      </>
    );
  }

  if (!exhibition) {
    return (
      <>
        <Header />
        <div className="min-h-screen flex flex-col items-center justify-center">
          <p className="text-amber-600 mb-4">画展不存在</p>
          <Button onClick={() => navigate('/exhibitions')}>返回画展列表</Button>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-amber-50" data-testid="exhibition-detail-page">
        {/* Hero Section */}
        <section className="relative h-[60vh] flex items-end">
          <div className="absolute inset-0">
            <img loading="lazy"
              src={getImageUrl(exhibition.cover_image) || 'https://images.unsplash.com/photo-1577720643272-265f09367456?w=1920'}
              alt={exhibition.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
          </div>

          {/* 返回按钮 */}
          <Button
            variant="ghost"
            className="absolute top-20 left-6 text-white hover:bg-amber-50/20"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> 返回
          </Button>
          
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-12">
            {exhibition.is_featured && (
              <Badge className="bg-amber-500/80 text-white mb-4">精选画展</Badge>
            )}
            <h1 className="text-4xl md:text-6xl font-light text-white mb-4">
              {getLocalizedField(exhibition, 'title')}
            </h1>
            <div className="flex flex-wrap items-center gap-6 text-white/70 text-sm">
              {exhibition.organizer && (
                <span>主办：{exhibition.organizer}</span>
              )}
              {exhibition.location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {exhibition.location}
                </span>
              )}
              {exhibition.start_date && (
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(exhibition.start_date)}
                  {exhibition.end_date && ` - ${formatDate(exhibition.end_date)}`}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {exhibition.view_count || 0} 次浏览
              </span>
            </div>
          </div>
        </section>

        {/* 简介 */}
        {exhibition.description && (
          <section className="py-16 px-6 border-b">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xl text-amber-700/80 leading-relaxed">
                {getLocalizedField(exhibition, 'description')}
              </p>
            </div>
          </section>
        )}

        {/* 作品展示 */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-light text-amber-900 mb-4">展出作品</h2>
              <p className="text-amber-600">共 {exhibition.artworks?.length || 0} 件作品</p>
              {/* 展览创建者可以添加作品 */}
              {canManageArtworks && (
                <Button 
                  onClick={handleOpenAddArtworks}
                  className="mt-4 bg-amber-600 hover:bg-amber-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  添加作品到展览
                </Button>
              )}
            </div>

            {exhibition.artworks?.length > 0 ? (
              <div className="columns-1 md:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6">
                {exhibition.artworks.map((artwork) => (
                  <div
                    key={artwork.id}
                    className="break-inside-avoid group cursor-pointer relative"
                  >
                    {/* 展览创建者可以移除作品 */}
                    {canManageArtworks && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleRemoveArtwork(artwork.id); }}
                        className="absolute top-2 left-2 z-20 w-7 h-7 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        title="从展览移除"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div 
                      className="relative rounded-xl overflow-hidden"
                      onClick={() => navigate(`/artwork/${artwork.id}`)}
                    >
                      <img loading="lazy"
                        src={getImageUrl(artwork.image_url)}
                        alt={getLocalizedField(artwork, 'title')}
                        className="w-full object-cover group-hover:scale-105 transition-transform duration-700"
                        style={{ minHeight: '200px' }}
                      />
                      
                      {/* 悬停遮罩 */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                        {/* 顶部操作 */}
                        <div className="absolute top-3 right-3 flex gap-2">
                          <button className="w-9 h-9 rounded-full bg-amber-50/20 backdrop-blur-md flex items-center justify-center hover:bg-amber-50/40 transition-colors">
                            <Heart className="w-4 h-4 text-white" />
                          </button>
                          <button className="w-9 h-9 rounded-full bg-amber-50/20 backdrop-blur-md flex items-center justify-center hover:bg-amber-50/40 transition-colors">
                            <Bookmark className="w-4 h-4 text-white" />
                          </button>
                        </div>
                        
                        {/* 底部信息 */}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          {artwork.creator_name && (
                            <div className="flex items-center gap-2 mb-2">
                              {artwork.creator_avatar ? (
                                <img loading="lazy"
                                  src={getImageUrl(artwork.creator_avatar)} 
                                  alt="" 
                                  className="w-6 h-6 rounded-full object-cover border border-white/30"
                                />
                              ) : (
                                <div className="w-6 h-6 rounded-full bg-amber-50/20 flex items-center justify-center">
                                  <User className="w-3 h-3 text-white" />
                                </div>
                              )}
                              <span className="text-xs text-white/80">{artwork.creator_name}</span>
                              {artwork.creator_type && (
                                <Badge variant="secondary" className="text-[10px] bg-amber-50/20 text-white">
                                  {getUserTypeLabel(artwork.creator_type)}
                                </Badge>
                              )}
                            </div>
                          )}
                          <h3 className="text-white font-medium mb-1 line-clamp-1">
                            {getLocalizedField(artwork, 'title')}
                          </h3>
                          {artwork.price && (
                            <span className="text-white font-medium">
                              ¥{artwork.price.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-amber-500">暂无作品</p>
              </div>
            )}
          </div>
        </section>

        {/* 添加作品弹窗 */}
        <Dialog open={showAddArtworks} onOpenChange={setShowAddArtworks}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>添加作品到展览</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {/* 搜索框 */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="搜索作品名称或艺术家..."
                  value={artworkSearch}
                  onChange={(e) => setArtworkSearch(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              
              {myArtworks.length > 0 ? (
                <>
                  <p className="text-sm text-amber-600 mb-4">
                    {isAdmin ? '平台全部作品：' : '您发布的作品：'}共 {myArtworks.filter(a => 
                      !artworkSearch || 
                      a.title?.toLowerCase().includes(artworkSearch.toLowerCase()) ||
                      a.artist?.toLowerCase().includes(artworkSearch.toLowerCase()) ||
                      a.creator_name?.toLowerCase().includes(artworkSearch.toLowerCase())
                    ).length} 件可添加（已选择 {selectedArtworks.length} 件）
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-[55vh] overflow-y-auto">
                    {myArtworks
                      .filter(a => 
                        !artworkSearch || 
                        a.title?.toLowerCase().includes(artworkSearch.toLowerCase()) ||
                        a.artist?.toLowerCase().includes(artworkSearch.toLowerCase()) ||
                        a.creator_name?.toLowerCase().includes(artworkSearch.toLowerCase())
                      )
                      .map(artwork => (
                      <div 
                        key={artwork.id}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          selectedArtworks.includes(artwork.id) 
                            ? 'border-amber-500 ring-2 ring-green-200' 
                            : 'border-transparent hover:border-amber-300'
                        }`}
                        onClick={() => toggleArtworkSelection(artwork.id)}
                      >
                        <img loading="lazy"
                          src={getImageUrl(artwork.image_url)}
                          alt={artwork.title}
                          className="w-full aspect-square object-cover"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                          <Checkbox 
                            checked={selectedArtworks.includes(artwork.id)}
                            className="w-6 h-6 bg-amber-50"
                          />
                        </div>
                        {selectedArtworks.includes(artwork.id) && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                        <div className="p-2 bg-amber-50">
                          <p className="text-sm font-medium truncate">{artwork.title}</p>
                          <p className="text-xs text-amber-600 truncate">{artwork.artist || artwork.creator_name || '未知艺术家'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex justify-end gap-3">
                    <Button variant="outline" onClick={() => { setShowAddArtworks(false); setArtworkSearch(''); }}>
                      取消
                    </Button>
                    <Button 
                      onClick={handleAddArtworks}
                      disabled={selectedArtworks.length === 0 || addingArtworks}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      {addingArtworks ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      添加 {selectedArtworks.length} 件作品
                    </Button>
                  </div>
                </>
              ) : (
                <div className="text-center py-10">
                  <p className="text-amber-600 mb-4">
                    {isAdmin ? '平台暂无可添加的作品' : '您还没有发布可添加的作品'}
                  </p>
                  <Button onClick={() => navigate(user?.id ? `/profile/${user.id}` : '/artworks')}>
                    去发布作品
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* 返回按钮 */}
        <section className="py-16 px-6 bg-amber-50/50">
          <div className="max-w-4xl mx-auto text-center">
            <Button
              variant="outline"
              className="rounded-full px-8 py-6 border-amber-400 text-amber-700 hover:bg-amber-600 hover:text-white hover:border-amber-600"
              onClick={() => navigate('/exhibitions')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              返回画展列表
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default ExhibitionDetailPage;
